import type { SiteSectionCategoryId, SiteSectionId, V4HSiteId } from "@/data/common/ids";
import type { CreatedSiteSectionCategory } from "@/domains/v4b/site-section/site-section.types";
import type { Ref } from "vue";

import { useCreateSectionCategoryMutation } from "@/composables/kili/sections/mutations/use-create-section-category-mutation";
import { useUpdateSectionsCategoriesMutation } from "@/composables/kili/sections/mutations/use-update-sections-categories-mutation";
import { useToaster } from "@/composables/layout/use-toaster";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

export function useSiteSectionCategory(siteId: Ref<V4HSiteId | undefined>) {
  const { t, k } = useTypedIntl();
  const toast = useToaster();
  const { mutateAsync: createCategory, isPending: isCreatingSectionCategory } =
    useCreateSectionCategoryMutation(siteId);
  const { mutateAsync: updateSectionsCategories, isPending: isUpdatingSectionsCategories } =
    useUpdateSectionsCategoriesMutation(siteId);

  async function createSiteSectionCategory(name: string): Promise<CreatedSiteSectionCategory | undefined> {
    try {
      const createdCategory = await createCategory({ name });

      toast.add({
        severity: "success",
        detail: t(k.tools.siteManagement.siteTreeStructure.categoryCreation.message.success),
      });

      MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_CREATED, {
        category_id: createdCategory.id,
      });

      return createdCategory;
    } catch (error) {
      toastKiliError(error, "tools.siteManagement.siteTreeStructure.categoryCreation.message.error");
      return undefined;
    }
  }

  async function assignSiteSectionCategory(
    sectionId: SiteSectionId,
    sectionCategoryId: SiteSectionCategoryId | null,
  ): Promise<boolean> {
    try {
      await updateSectionsCategories({
        sectionsWithCategories: [{ sectionId, sectionCategoryId }],
      });

      const successMessage =
        sectionCategoryId === null
          ? k.tools.siteManagement.siteTreeStructure.categoryAssignment.message.removalSuccess
          : k.tools.siteManagement.siteTreeStructure.categoryAssignment.message.success;

      toast.add({
        severity: "success",
        detail: t(successMessage),
      });

      MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_ASSIGNED_TO_SECTION, {
        action_type: "single",
        space_id: sectionId,
        action: sectionCategoryId ? "assigned" : "removed",
        category_id: sectionCategoryId ?? undefined,
      });

      return true;
    } catch (error) {
      toastKiliError(error, "tools.siteManagement.siteTreeStructure.categoryAssignment.message.error");
      return false;
    }
  }

  return {
    assignSiteSectionCategory,
    createSiteSectionCategory,
    isCreatingSectionCategory,
    isUpdatingSectionsCategories,
  };
}
